/**
 * Print-Only Gap Analysis Page for PDF Generation
 * Renders profit/value/wealth gap analysis for PDF export
 *
 * URL: /print/gap-analysis/:type/:businessId
 * Type: 'profit', 'value', or 'wealth'
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';

const PrintGapAnalysis = () => {
  const { type, businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const gapTypes = {
    profit: { title: 'Profit Gap Analysis', endpoint: 'profit-gaps', color: '#10b981' },
    value: { title: 'Value Gap Analysis', endpoint: 'value-gaps', color: '#3b82f6' },
    wealth: { title: 'Wealth Gap Analysis', endpoint: 'wealth-gaps', color: '#8b5cf6' }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter (for PDF generation) or localStorage (for direct viewing)
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const [businessRes, gapRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/businesses/${businessId}`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${gapTypes[type].endpoint}?business_id=${businessId}`, { headers })
        ]);

        if (!businessRes.ok || !gapRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const businessData = await businessRes.json();
        const gapData = await gapRes.json();

        setData({
          business: businessData,
          gap: Array.isArray(gapData) ? gapData[0] : gapData
        });

        setLoading(false);
      } catch (err) {
        console.error('Error loading gap analysis data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [type, businessId]);

  useEffect(() => {
    if (!loading && data) {
      document.body.setAttribute('data-pdf-ready', 'true');
    }
  }, [loading, data]);

  if (loading) {
    return (
      <div className="print-container">
        <div className="loading">Loading gap analysis data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const { business, gap } = data;
  const { title, color } = gapTypes[type];
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>{title}</h1>
          <div className="report-type">Strategic Business Analysis</div>
        </div>

        <div className="cover-business-info">
          <h2>{business.name || business.business_name}</h2>
          <p className="industry">{business.industry}</p>
        </div>

        {type === 'profit' && gap && (
          <div className="cover-value" style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
            <div className="value-label">Profit Gap Opportunity</div>
            <div className="value-amount">{formatCurrency(gap.valuation_impact_best || gap.valuation_gap || 0)}</div>
          </div>
        )}

        {type === 'value' && gap && (
          <div className="cover-value" style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
            <div className="value-label">Value Gap Opportunity</div>
            <div className="value-amount">{formatCurrency(gap.value_gap || 0)}</div>
          </div>
        )}

        {type === 'wealth' && gap && (
          <div className="cover-value" style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
            <div className="value-label">Wealth Gap</div>
            <div className="value-amount">{formatCurrency(gap.calculated_wealth_gap || 0)}</div>
          </div>
        )}

        <div className="cover-footer">
          <p>Generated {today}</p>
          <p className="confidential">Confidential Gap Analysis Report</p>
        </div>
      </div>

      {/* Analysis Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="gap-analysis-header" style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
          <h1>{title}</h1>
          <p>Strategic analysis for {business.name || business.business_name}</p>
        </div>

        {type === 'profit' && gap && (
          <>
            <h2 className="section-title">Current Performance</h2>
            <div className="gap-metric-row">
              <div className="gap-metric">
                <div className="gap-metric-label">Current Revenue</div>
                <div className="gap-metric-value">{formatCurrency(gap.current_revenue)}</div>
              </div>
              <div className="gap-metric">
                <div className="gap-metric-label">Current EBITDA</div>
                <div className="gap-metric-value">{formatCurrency(gap.current_ebitda)}</div>
              </div>
            </div>
            <div className="gap-metric-row">
              <div className="gap-metric">
                <div className="gap-metric-label">Current Margin</div>
                <div className="gap-metric-value">{gap.current_ebitda_margin?.toFixed(1)}%</div>
              </div>
              <div className="gap-metric">
                <div className="gap-metric-label">Current Valuation</div>
                <div className="gap-metric-value">{formatCurrency(gap.current_valuation)}</div>
              </div>
            </div>

            <h2 className="section-title" style={{ marginTop: '0.4in' }}>Industry Benchmarks</h2>
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Current</th>
                  <th>Industry Median</th>
                  <th>Best in Class</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>EBITDA Margin</td>
                  <td>{gap.current_ebitda_margin?.toFixed(1)}%</td>
                  <td>{gap.industry_median_ebitda_margin?.toFixed(1)}%</td>
                  <td>{gap.industry_high_ebitda_margin?.toFixed(1)}%</td>
                </tr>
                <tr>
                  <td>Target EBITDA</td>
                  <td>{formatCurrency(gap.current_ebitda)}</td>
                  <td>{formatCurrency(gap.target_ebitda_median)}</td>
                  <td>{formatCurrency(gap.target_ebitda_best)}</td>
                </tr>
                <tr>
                  <td>Business Valuation</td>
                  <td>{formatCurrency(gap.current_valuation)}</td>
                  <td>{formatCurrency(gap.median_valuation)}</td>
                  <td>{formatCurrency(gap.best_valuation)}</td>
                </tr>
              </tbody>
            </table>

            <div className="disclosure-box" style={{ marginTop: '0.3in' }}>
              <h3>Opportunity Summary</h3>
              <p>
                By improving your EBITDA margin to industry median levels, you could increase your business value by{' '}
                <strong>{formatCurrency(gap.valuation_impact_median)}</strong>.
              </p>
              <p>
                Reaching best-in-class performance could add{' '}
                <strong>{formatCurrency(gap.valuation_impact_best)}</strong> to your business value.
              </p>
            </div>
          </>
        )}

        {type === 'value' && gap && (
          <>
            <h2 className="section-title">Valuation Analysis</h2>
            <div className="gap-metric-row">
              <div className="gap-metric">
                <div className="gap-metric-label">Valuation Range (Low)</div>
                <div className="gap-metric-value">{formatCurrency(gap.valuation_low)}</div>
              </div>
              <div className="gap-metric">
                <div className="gap-metric-label">Valuation Range (High)</div>
                <div className="gap-metric-value">{formatCurrency(gap.valuation_high)}</div>
              </div>
            </div>
            <div className="gap-metric-row">
              <div className="gap-metric">
                <div className="gap-metric-label">Current Exit Value</div>
                <div className="gap-metric-value">{formatCurrency(gap.current_exit_value)}</div>
              </div>
              <div className="gap-metric">
                <div className="gap-metric-label">Best-in-Class Value</div>
                <div className="gap-metric-value">{formatCurrency(gap.best_in_class_value)}</div>
              </div>
            </div>

            <h2 className="section-title" style={{ marginTop: '0.4in' }}>Attractiveness Scores</h2>
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Attractiveness Score</th>
                  <th>Estimated Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Current</td>
                  <td>{gap.current_attractiveness?.toFixed(1)}%</td>
                  <td>{formatCurrency(gap.current_exit_value)}</td>
                </tr>
                <tr>
                  <td>Green Zone (67%)</td>
                  <td>{gap.green_zone_attractiveness?.toFixed(1)}%</td>
                  <td>{formatCurrency(gap.green_zone_value)}</td>
                </tr>
                <tr>
                  <td>Best-in-Class (72%)</td>
                  <td>{gap.best_in_class_attractiveness?.toFixed(1)}%</td>
                  <td>{formatCurrency(gap.best_in_class_value)}</td>
                </tr>
              </tbody>
            </table>

            <div className="disclosure-box" style={{ marginTop: '0.3in' }}>
              <h3>Value Gap Opportunity</h3>
              <p>
                Your business has a value gap of <strong>{formatCurrency(gap.value_gap)}</strong> between current
                performance and best-in-class potential.
              </p>
              <p>
                Reaching the green zone (67% attractiveness) would add{' '}
                <strong>{formatCurrency(gap.value_impact_green_zone)}</strong> to your exit value.
              </p>
            </div>
          </>
        )}

        {type === 'wealth' && gap && (
          <>
            <h2 className="section-title">Financial Goals</h2>
            <div className="gap-metric-row">
              <div className="gap-metric">
                <div className="gap-metric-label">Wealth Goal</div>
                <div className="gap-metric-value">{formatCurrency(gap.calculated_wealth_goal)}</div>
              </div>
              <div className="gap-metric">
                <div className="gap-metric-label">Current Net Worth</div>
                <div className="gap-metric-value">{formatCurrency(gap.current_net_worth)}</div>
              </div>
            </div>
            <div className="gap-metric-row">
              <div className="gap-metric negative">
                <div className="gap-metric-label">Wealth Gap</div>
                <div className="gap-metric-value">{formatCurrency(gap.calculated_wealth_gap)}</div>
              </div>
              <div className="gap-metric">
                <div className="gap-metric-label">Exit Year</div>
                <div className="gap-metric-value">{gap.exit_year}</div>
              </div>
            </div>

            {gap.required_sale_price_data && (
              <>
                <h2 className="section-title" style={{ marginTop: '0.4in' }}>Required Business Sale Analysis</h2>
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Required Sale Price</td>
                      <td>{formatCurrency(gap.required_sale_price_data.required_sale_price)}</td>
                    </tr>
                    <tr>
                      <td>Total Capital Needed</td>
                      <td>{formatCurrency(gap.required_sale_price_data.total_capital_needed)}</td>
                    </tr>
                    <tr>
                      <td>Future Net Worth (at exit)</td>
                      <td>{formatCurrency(gap.required_sale_price_data.future_net_worth)}</td>
                    </tr>
                    <tr>
                      <td>Net Gap from Business</td>
                      <td>{formatCurrency(gap.required_sale_price_data.net_gap_from_business)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            <div className="disclosure-box" style={{ marginTop: '0.3in' }}>
              <h3>Wealth Gap Summary</h3>
              <p>
                To achieve your wealth goal of <strong>{formatCurrency(gap.calculated_wealth_goal)}</strong>, you have
                a gap of <strong>{formatCurrency(gap.calculated_wealth_gap)}</strong> to close.
              </p>
              {gap.required_sale_price_data && (
                <p>
                  Your business needs to sell for approximately{' '}
                  <strong>{formatCurrency(gap.required_sale_price_data.required_sale_price)}</strong>{' '}
                  to meet your financial goals.
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Recommendations Page */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Action Plan & Recommendations</h1>

        <div className="next-steps">
          <div className="step">
            <h3>1. Understand Your Gaps</h3>
            <p>
              Review the analysis above to understand where your business stands relative to benchmarks and goals.
              Identify the specific areas that represent the greatest opportunities for improvement.
            </p>
          </div>

          <div className="step">
            <h3>2. Prioritize High-Impact Actions</h3>
            <ul>
              <li>Focus on improving profitability margins through operational efficiency</li>
              <li>Reduce key risk factors (customer concentration, owner dependency)</li>
              <li>Strengthen systems, processes, and documentation</li>
              <li>Build a stronger management team</li>
            </ul>
          </div>

          <div className="step">
            <h3>3. Create a Timeline</h3>
            <p>
              Develop a 12-24 month action plan with specific milestones. Small consistent improvements compound
              significantly over time.
            </p>
          </div>

          <div className="step">
            <h3>4. Engage Expert Advisors</h3>
            <ul>
              <li>Work with a Certified Exit Planning Advisor (CEPA) for strategic guidance</li>
              <li>Consult financial advisors for wealth planning</li>
              <li>Consider M&A advisors when you're ready to actively pursue a transaction</li>
            </ul>
          </div>

          <div className="step">
            <h3>5. Track Progress</h3>
            <p>
              Re-run this analysis quarterly to monitor improvements. Celebrate wins and adjust strategies as needed.
            </p>
          </div>
        </div>

        <div className="footer-branding">
          <p>Exit Ready Pro - Gap Analysis Dashboard</p>
          <p className="footer-date">{today}</p>
        </div>
      </div>
    </div>
  );
};

export default PrintGapAnalysis;
